import React from "react";

import { ColumnType } from "antd/es/table";

import { formatNumber, trimToDecimals, useTranslation } from "utils";

import { FOOTPRINTING_CATEGORY_COLORS } from "consts";

import { FootprintingPorkEmissionsSourceCategory, FootprintingPorkResultsDataTableRow, PorkFootprintCalculationSummary } from "models";

import { Dot, Flex, Icon, Table, TooltipWrapper } from "components";

type FootprintingPorkResultTableProps = {
  footprintCalculationSummary?: PorkFootprintCalculationSummary;
};

type TableDataRow = {
  key: React.Key;
  scope: string;
  source: React.Key;
  dot: React.ReactNode | null;
  totalCO2e: string;
  perKgYieldCO2e: string;
  perPigCO2e: string;
  shareOfTotal: string;
};

interface ColumnConfiguration extends ColumnType {
  title: React.ReactNode | string | null;
  dataIndex: keyof TableDataRow;
}

type CalculationSummaryTuple = [FootprintingPorkEmissionsSourceCategory, FootprintingPorkResultsDataTableRow];

const PORK_SCOPE_MAPPING: Record<FootprintingPorkEmissionsSourceCategory, number> = {
  feeding: 1,
  manureManagement: 1,
  stockRenewal: 1,
  energy: 1,
  waste: 1,
  luc: 1,
};

export const FootprintingPorkResultTable = ({ footprintCalculationSummary }: FootprintingPorkResultTableProps) => {
  const { t } = useTranslation(["footprintingPork", "footprinting"]);

  const columns: ColumnConfiguration[] = [
    { title: null, dataIndex: "dot" },
    { title: t("resultsPage.table.columnHeader.source"), dataIndex: "source" },
    {
      title: (
        <Flex gap={4}>
          {t("resultsPage.table.columnHeader.scope")}
          <TooltipWrapper className="scope-info-icon-container" tooltip={{ title: t("resultsPage.table.columnHeader.scopeTooltip"), placement: "top" }}>
            <Icon className="scope-info-icon" iconName="tabler/info-circle" size="s" />
          </TooltipWrapper>
        </Flex>
      ),
      dataIndex: "scope",
    },
    { title: t("resultsPage.table.columnHeader.totalCO2e"), dataIndex: "totalCO2e" },
    { title: t("resultsPage.table.columnHeader.perKgYieldCO2e"), dataIndex: "perKgYieldCO2e" },
    { title: t("resultsPage.table.columnHeader.perPigCO2e"), dataIndex: "perPigCO2e" },
    { title: t("resultsPage.table.columnHeader.shareOfTotal"), dataIndex: "shareOfTotal" },
  ];

  if (!footprintCalculationSummary) {
    return null;
  }

  const tableData = (Object.entries(footprintCalculationSummary.tableData) as CalculationSummaryTuple[])
    .sort((a, b) => b[1].data.totalCO2e - a[1].data.totalCO2e)
    .map(
      ([category, row]): TableDataRow => ({
        key: category,
        source: t(`categories.${category}`),
        scope: t("resultsPage.table.rowValue.scope", { scope: PORK_SCOPE_MAPPING[category] }),
        dot: <Dot color={FOOTPRINTING_CATEGORY_COLORS[category]} />,
        totalCO2e: formatNumber({ number: trimToDecimals(row.data.totalCO2e, 2) }),
        perKgYieldCO2e: formatNumber({ number: trimToDecimals(row.data.perKgYieldCO2e, 2) }),
        perPigCO2e: formatNumber({ number: trimToDecimals(row.data.perPigCO2e, 2) }),
        shareOfTotal: formatNumber({ number: trimToDecimals(100 * row.data.shareOfTotal, 2) }),
      })
    );

  return (
    <Table
      className="flex-1"
      data={tableData}
      columns={columns}
      expandable={{ expandedRowRender: () => <div className="results-table-hide-expanded-row" /> }}
    />
  );
};
